/**
 * COMPTABILITÉ — UN DOCUMENT SOURCE, LIGNE À LIGNE.
 *
 * Cet écran répond à deux questions, dans cet ordre :
 *   1. POURQUOI ce document attend-il quelqu'un ? — une raison qu'un humain
 *      peut traiter : quelle caisse est inconnue, quel moyen de paiement n'a
 *      pas été reconnu, quel total ne tombe pas juste (attendu, calculé, écart),
 *      quelle nuit reste à trancher. Jamais « erreur d'analyse » tout court :
 *      cette phrase-là n'apprend rien et fait rouvrir le PDF.
 *   2. QU'A LU l'analyseur ? — caisse par caisse, moyen de paiement par moyen de
 *      paiement, au centime, avec le résultat des contrôles de cohérence.
 *
 * ═══ LE NOM DU FOURNISSEUR N'EST JAMAIS LE NOM COURANT ═══
 * « Caisse Bar … 2 » est un libellé FOURNISSEUR. Il s'affiche en second rang,
 * sous le nom canonique de la caisse. Tant que la correspondance n'est pas
 * faite, la ligne dit « Caisse inconnue » et propose de la rattacher — elle ne
 * prend jamais le libellé du fournisseur pour un nom de caisse.
 *
 * ═══ UNE LECTURE NE SE CORRIGE PAS, ELLE SE REFAIT ═══
 * Le rôle applicatif n'a ni UPDATE ni DELETE sur les lectures. Rattacher une
 * caisse ne réécrit donc PAS la lecture affichée : la correspondance est
 * mémorisée, puis le document est relu, et c'est la nouvelle lecture qui porte
 * le rattachement. Les précédentes restent lisibles, avec leur analyseur.
 *
 * ROUTES CONSOMMÉES (`apps/api/src/routes/finance.ts`) :
 *   GET  /api/v1/finance/sources/:id                              finance.source.read
 *   POST /api/v1/finance/sources/:id/parse                        finance.source.manage
 *   POST /api/v1/finance/sources/:id/attach                       finance.source.manage
 *   GET  /api/v1/finance/references                               finance.reference.read
 *   POST /api/v1/finance/references/registers/:id/aliases         finance.reference.manage (step-up)
 */

import { useMemo, useState } from 'react'
import { AlertTriangle, FileText, Info, Link2 } from 'lucide-react'
import { SmartBack } from '../navigation/SmartBack.js'
import { Link } from '../router.js'
import { Combobox, DatePicker } from '../admin/pickers.js'
import { Modal, adminMutate, usePermissions, useToast } from '../admin/primitives.js'
import { Checkbox } from '../admin/form.js'
import {
  Ecart, Montant, aujourdhuiParis, heureParis, ilYA, nuitCourte, nuitLongue,
} from './ClosingCommon.js'
import { EtatBloc, cents, estObjet, texte, useRessource } from './RevenueShared.js'
import { formatMoney, sommeCents } from './money.js'
import {
  libelleEtat, libelleProvenance, libelleType, lireDoc, pastilleEtat, type DocRecu,
} from './FinanceSourcesPage.js'
import { Ecarter } from './SourcesEcart.js'
import { DeplacerRapport } from './DeplacerRapport.js'
import { VoirSource } from './SourceViewer.js'
import { RedditionResume, lireReddition, type RedditionVue } from './RedditionResume.js'
import './finance.css'
import './finance-revenue.css'
import './finance-sources.css'

/* ═══════════════════ le vocabulaire des moyens de paiement ═══════════════ */

/**
 * Miroir des codes semés en base (`finance_payment_method`). C'est une
 * TRADUCTION de code, pas une donnée : un code absent d'ici s'affiche tel quel
 * plutôt que d'être rapproché du plus ressemblant. Le libellé du fournisseur,
 * lui, vit sur la ligne et reste affiché en second rang.
 */
const MOYENS: Record<string, string> = {
  CB: 'Carte bancaire',
  ESPECES: 'Espèces',
  OFFERT: 'Offerts',
  CHEQUE: 'Chèque',
  FACTURE: 'Facture',
  VIREMENT: 'Virement',
}

/** Le code du moyen « offert ». Il sert à DÉCIDER, il ne s'affiche jamais. */
const CODE_OFFERT = 'OFFERT'

function libelleMoyen(code: string | null): string {
  if (code === null) return 'Non rattaché'
  return MOYENS[code] ?? code
}

/* ═══════════════════════ les raisons, en français ════════════════════════ */

/**
 * CE QUE CHAQUE ANOMALIE VEUT DIRE, ET CE QU'ON EN FAIT.
 *
 * Les cinq premiers codes sont ceux que le lecteur ZAP émet
 * (`packages/core/src/finance/zap-parser.ts`) ; les suivants ceux que la chaîne
 * d'ingestion peut produire. Un code absent de cette table n'est PAS masqué :
 * il s'affiche avec le détail transmis par le lecteur, et l'écran dit qu'aucune
 * consigne n'est encore écrite pour lui.
 */
const RAISONS: Record<string, { titre: string; quoiFaire: string }> = {
  TOTAL_CAISSE_INCOHERENT: {
    titre: 'Le total d’une caisse ne tombe pas juste',
    quoiFaire: 'Comparez l’attendu et le calculé ci-dessous avec le rapport papier : un moyen de paiement manque, ou un montant a été lu de travers. Rien n’entre en clôture tant que l’écart n’est pas expliqué.',
  },
  TOTAL_DOCUMENT_INCOHERENT: {
    titre: 'Le total du document ne tombe pas juste',
    quoiFaire: 'La somme des caisses ne correspond pas au total annoncé par le rapport. Vérifiez qu’aucune caisse ne manque à la lecture avant de vous servir de ces montants.',
  },
  MONTANT_ILLISIBLE: {
    titre: 'Un montant n’a pas pu être lu',
    quoiFaire: 'Le montant est resté hors de la lecture : il n’a pas été deviné, il n’a pas été compté. Relevez-le sur le rapport et saisissez-le dans la clôture de la nuit.',
  },
  PERIODE_ABSENTE: {
    titre: 'La période du rapport est absente',
    quoiFaire: 'Sans période, la nuit commerciale ne peut pas être déduite : rattachez le document à la bonne nuit ci-dessous, en vous fiant à l’heure d’édition du rapport.',
  },
  AUCUNE_CAISSE: {
    titre: 'Aucune caisse n’a été reconnue',
    quoiFaire: 'Le document a bien été reçu, mais sa structure n’a pas été retrouvée. Vérifiez qu’il s’agit du bon rapport ; sinon, signalez-le : c’est le format du fournisseur qui a changé.',
  },
  AUCUN_LECTEUR: {
    titre: 'Aucun lecteur n’est installé pour ce format',
    quoiFaire: 'Le document est conservé et horodaté, mais rien n’en a été extrait : les montants se saisissent à la main dans la clôture tant que ce format n’est pas lu.',
  },
  FORMAT_INCONNU: {
    titre: 'Le format du document n’a pas été reconnu',
    quoiFaire: 'Vérifiez qu’il s’agit bien d’un rapport attendu. Un fichier d’un autre type reste archivé, mais il n’alimentera aucun chiffre.',
  },
  CAISSE_INCONNUE: {
    titre: 'Une caisse du rapport n’est rattachée à aucune caisse de Gestion',
    quoiFaire: 'Rattachez-la ci-dessous à une caisse existante. La correspondance peut être mémorisée : les rapports suivants se résoudront alors seuls.',
  },
  MOYEN_INCONNU: {
    titre: 'Un moyen de paiement n’a pas été reconnu',
    quoiFaire: 'Le libellé et le montant sont conservés tels quels, sans rapprochement par ressemblance. La correspondance se déclare dans les référentiels.',
  },
  NUIT_AMBIGUE: {
    titre: 'La nuit commerciale est ambiguë',
    quoiFaire: 'Un rapport tiré au petit matin décrit la nuit de la veille. Tranchez ci-dessous : c’est un jugement humain, pas une donnée du fournisseur.',
  },
  PERIODE_MULTI_NUITS: {
    titre: 'La période du rapport couvre plusieurs nuits',
    quoiFaire: 'Une caisse n’a pas été clôturée : le rapport court sur plusieurs nuits, et les soirées comme les heures des tickets (ci-dessus) ne suffisent pas à trancher seules. Choisissez sa nuit avec « Déplacer », ci-dessous ou sur la fiche de la nuit : la synthèse, ses annexes, ses tickets et ses produits passent ensemble. Garder la nuit actuelle est aussi une décision, qui lève ce signalement.',
  },
  NUIT_DIVERGENTE: {
    titre: 'La lecture range ce rapport sur une autre nuit',
    quoiFaire: 'Le rattachement existant est conservé : une lecture ne le réécrit jamais. Tranchez ci-dessous. Pour un rapport ZAP, « Déplacer » fait passer le rapport entier (annexes et tickets compris) sur la bonne nuit — ou, vers la nuit actuelle, l’enregistre comme décision : la relecture ne la contestera plus. Pour un autre document, « Rattacher à cette nuit ».',
  },
}

/** « CAISSE_INCONNUE » → « Caisse inconnue ». Mieux qu'un code brut à l'écran. */
function humaniser(code: string): string {
  const mots = code.toLowerCase().replace(/_/g, ' ')
  return mots.charAt(0).toUpperCase() + mots.slice(1)
}

export interface Raison {
  cle: string
  /** Le code transmis par le lecteur, quand il en a transmis un. */
  code: string | null
  titre: string
  /** Le fait, tel que le lecteur l'a transmis. */
  detail: string | null
  /** Ce qu'un humain peut en faire. `null` = aucune consigne écrite pour ce code. */
  quoiFaire: string | null
  attendu: number | null
  calcule: number | null
  ecart: number | null
  /** Ce que le lecteur a envoyé quand on n'a pas su le nommer. */
  brut: string | null
}

/**
 * `unmapped` traverse l'API en TEXTE (le schéma de sortie déclare des chaînes,
 * et tout objet y est encodé en JSON). On rouvre donc l'enveloppe : un objet
 * structuré redevient une raison lisible, une chaîne libre reste une chaîne.
 */
function objetAnomalie(v: unknown): Record<string, unknown> | null {
  if (estObjet(v)) return v
  if (typeof v !== 'string') return null
  const s = v.trim()
  if (!s.startsWith('{')) return null
  try {
    const parse: unknown = JSON.parse(s)
    return estObjet(parse) ? parse : null
  } catch {
    return null
  }
}

export function lireRaison(v: unknown, index: number): Raison {
  const o = objetAnomalie(v)
  if (o === null) {
    const brut = typeof v === 'string' ? v : JSON.stringify(v)
    return {
      cle: `brut-${index}`,
      code: null,
      titre: 'Le lecteur a signalé un élément qu’il n’a pas su ranger',
      detail: typeof v === 'string' && v.trim() !== '' ? v : null,
      quoiFaire: 'Aucun code d’anomalie n’accompagne ce signalement : transmettez la ligne ci-dessous telle quelle pour que le lecteur apprenne à la nommer.',
      attendu: null, calcule: null, ecart: null,
      brut: typeof v === 'string' ? null : brut,
    }
  }
  const code = texte(o['code']) ?? texte(o['raison'])
  const detail = texte(o['detail']) ?? texte(o['message']) ?? texte(o['explication'])
  const connue = code === null ? undefined : RAISONS[code]
  return {
    cle: `${code ?? 'sans-code'}-${index}`,
    code,
    titre: connue?.titre ?? (code === null ? 'Élément non compris par le lecteur' : humaniser(code)),
    detail,
    quoiFaire: connue?.quoiFaire
      ?? (code === null
        ? 'Ce signalement n’est pas nommé : le détail ci-dessus est tout ce que le lecteur a transmis.'
        : 'Aucune consigne n’est encore écrite dans Gestion pour ce code : le détail ci-dessus est ce que le lecteur a transmis.'),
    attendu: cents(o['expectedCents']) ?? cents(o['attendu_cents']),
    calcule: cents(o['computedCents']) ?? cents(o['calcule_cents']),
    ecart: cents(o['deltaCents']) ?? cents(o['ecart_cents']),
    // Sans détail lisible, on montre la charge utile TELLE QUELLE : mieux vaut
    // un objet brut qu'une phrase inventée sur ce que le lecteur a voulu dire.
    brut: detail === null ? JSON.stringify(o) : null,
  }
}

/* ═══════════════════════ ce que l'écran lit ═══════════════════════ */

export interface LigneLue {
  id: string
  aliasCaisse: string | null
  caisseId: string | null
  caisseNom: string | null
  moyen: string | null
  montant: number | null
  libelle: string | null
}

export interface Lecture {
  id: string
  analyseur: string | null
  statut: string
  note: string | null
  creeLe: string | null
  raisons: Raison[]
  lignes: LigneLue[]
}

export interface DetailDoc extends DocRecu {
  taille: number | null
  mime: string | null
  empreinte: string | null
  lectures: Lecture[]
  /** Ce que dit une reddition Shotgun lue ; `null` pour tout autre document. */
  reddition: RedditionVue | null
  /** Le rapport ZAP dont ce document est la synthèse ou une pièce ; `null` sinon. */
  rapportZap: RapportZap | null
}

/** Le rapport ZAP d'un document, tel que le serveur le décrit (`zap_report`). */
export interface RapportZap {
  syntheseId: string
  syntheseNom: string
  syntheseNuit: string | null
  /** Les nuits que la période de la synthèse couvre ; `null` si elle ne les dit pas. */
  nuitsCouvertes: string[] | null
  periode: { debut: string; fin: string } | null
}

function lireRapportZap(v: unknown): RapportZap | null {
  if (!estObjet(v)) return null
  const id = texte(v['summary_id'])
  const nom = texte(v['summary_filename'])
  if (id === null || nom === null) return null
  const nc = Array.isArray(v['nights_covered']) ? v['nights_covered'].filter((x): x is string => typeof x === 'string') : null
  const debut = texte(v['period_start'])
  const fin = texte(v['period_end'])
  return {
    syntheseId: id, syntheseNom: nom, syntheseNuit: texte(v['summary_business_date']),
    nuitsCouvertes: nc !== null && nc.length > 0 ? nc : null,
    periode: debut !== null && fin !== null ? { debut, fin } : null,
  }
}

function lireLigne(v: unknown): LigneLue | null {
  if (!estObjet(v)) return null
  const id = texte(v['id'])
  if (id === null) return null
  return {
    id,
    aliasCaisse: texte(v['register_alias']),
    caisseId: texte(v['register_id']),
    caisseNom: texte(v['register_name']),
    moyen: texte(v['payment_method']),
    montant: cents(v['amount_cents']),
    libelle: texte(v['label']),
  }
}

function lireLecture(v: unknown): Lecture | null {
  if (!estObjet(v)) return null
  const id = texte(v['id'])
  if (id === null) return null
  const brutRaisons = Array.isArray(v['unmapped']) ? v['unmapped'] : []
  const brutLignes = Array.isArray(v['lines']) ? v['lines'] : []
  const lignes: LigneLue[] = []
  for (const l of brutLignes) {
    const ligne = lireLigne(l)
    if (ligne !== null) lignes.push(ligne)
  }
  return {
    id,
    analyseur: texte(v['parser_version']),
    statut: texte(v['status']) ?? 'NEEDS_REVIEW',
    note: texte(v['note']),
    creeLe: texte(v['created_at']),
    raisons: brutRaisons.map((x, i) => lireRaison(x, i)),
    lignes,
  }
}

function lireDetail(v: unknown): DetailDoc | null {
  if (!estObjet(v)) return null
  const base = lireDoc(v)
  if (base === null) return null
  const brut = Array.isArray(v['readings']) ? v['readings'] : []
  const lectures: Lecture[] = []
  for (const r of brut) {
    const lecture = lireLecture(r)
    if (lecture !== null) lectures.push(lecture)
  }
  return {
    ...base,
    taille: cents(v['byte_size']),
    mime: texte(v['mime']),
    empreinte: texte(v['sha256']),
    lectures,
    reddition: lireReddition(v['reddition']),
    rapportZap: lireRapportZap(v['zap_report']),
  }
}

/* ═══════════════════════ les caisses du référentiel ═══════════════════════ */

interface Caisse { id: string; nom: string; pointDeVente: string | null }

function lireCaisses(v: unknown): Caisse[] | null {
  if (!estObjet(v) || !Array.isArray(v['registers'])) return null
  const out: Caisse[] = []
  for (const r of v['registers']) {
    if (!estObjet(r)) continue
    const id = texte(r['id'])
    const nom = texte(r['name'])
    if (id === null || nom === null) continue
    out.push({ id, nom, pointDeVente: texte(r['point_of_sale_name']) })
  }
  return out
}

/* ═══════════════════════ regroupement par caisse ═══════════════════════ */

interface GroupeCaisse {
  cle: string
  /** Le nom canonique. `null` tant que la caisse n'est pas rattachée. */
  nom: string | null
  /** Le libellé du fournisseur, TOUJOURS en second rang. */
  alias: string | null
  lignes: LigneLue[]
  /**
   * Somme des lignes portant un moyen de paiement reconnu. `null` quand AUCUNE
   * ne l'est : une caisse dont rien n'a été rapproché ne vaut pas zéro euro,
   * elle vaut « on ne sait pas » — et « — » le dit, « 0,00 € » le cache.
   */
  sommeReconnue: number | null
  /** Lignes qu'aucun moyen de paiement ne porte : jamais additionnées. */
  sansMoyen: LigneLue[]
  offerts: number
}

function grouperParCaisse(lignes: LigneLue[]): GroupeCaisse[] {
  const par = new Map<string, GroupeCaisse>()
  for (const l of lignes) {
    const cle = l.caisseId ?? (l.aliasCaisse === null ? 'sans-caisse' : `alias:${l.aliasCaisse}`)
    let g = par.get(cle)
    if (g === undefined) {
      g = { cle, nom: l.caisseNom, alias: l.aliasCaisse, lignes: [], sommeReconnue: null, sansMoyen: [], offerts: 0 }
      par.set(cle, g)
    }
    g.lignes.push(l)
    if (l.moyen === null) g.sansMoyen.push(l)
    else {
      g.sommeReconnue = (g.sommeReconnue ?? 0) + (l.montant ?? 0)
      if (l.moyen === CODE_OFFERT) g.offerts += l.montant ?? 0
    }
  }
  return [...par.values()]
}

/* ═══════════════════════ l'écran ═══════════════════════ */

export function FinanceSourceDetailPage(props: { id: string }) {
  const toast = useToast()
  const { permissions, loading } = usePermissions()
  const peutGerer = !loading && permissions.includes('finance.source.manage')
  const peutMapper = !loading && permissions.includes('finance.reference.manage')

  const res = useRessource<DetailDoc>(`/api/v1/finance/sources/${props.id}`, lireDetail)
  const doc = res.etat.phase === 'ok' ? res.etat.data : null

  const [relecture, setRelecture] = useState(false)
  const [aRattacher, setARattacher] = useState<string | null>(null)
  const [nuitChoisie, setNuitChoisie] = useState<string | null>(null)
  const [rattachement, setRattachement] = useState(false)

  const lecture = doc?.lectures[0] ?? null
  const groupes = useMemo(() => grouperParCaisse(lecture?.lignes ?? []), [lecture])
  const inconnues = groupes.filter((g) => g.nom === null && g.alias !== null)

  /* Le référentiel n'est lu QUE s'il sert : une caisse inconnue et le droit de
     déclarer la correspondance. Sinon la requête ne part pas. */
  const refs = useRessource<Caisse[]>(
    inconnues.length > 0 && peutMapper ? '/api/v1/finance/references' : null,
    lireCaisses,
  )

  const relire = async () => {
    setRelecture(true)
    const r = await adminMutate<{ error?: string; message?: string; status?: string }>(
      'POST', `/api/v1/finance/sources/${props.id}/parse`,
    )
    setRelecture(false)
    if (r.status >= 200 && r.status < 300) {
      toast('ok', r.data.status === 'PARSED'
        ? 'Document relu : tout a été compris cette fois.'
        : 'Document relu. Une nouvelle lecture s’ajoute à l’historique.')
      res.recharger()
      return
    }
    toast('error', r.data.message ?? 'La relecture a été refusée par le serveur.')
  }

  const rattacherNuit = async (nuit: string) => {
    setRattachement(true)
    const r = await adminMutate<{ error?: string; message?: string }>(
      'POST', `/api/v1/finance/sources/${props.id}/attach`, { business_date: nuit },
    )
    setRattachement(false)
    if (r.status >= 200 && r.status < 300) {
      toast('ok', `Document rattaché à la nuit du ${nuitCourte(nuit)}.`)
      setNuitChoisie(null)
      res.recharger()
      return
    }
    toast('error', r.data.message ?? 'Le rattachement a été refusé par le serveur.')
  }

  /* ─── les raisons : celles du lecteur, plus celles que les lignes prouvent ─── */
  const raisons: Raison[] = []
  if (lecture !== null) raisons.push(...lecture.raisons)
  const codes = new Set(raisons.map((r) => r.code).filter((c): c is string => c !== null))
  if (inconnues.length > 0 && !codes.has('CAISSE_INCONNUE') && !codes.has('REGISTRE_INCONNU')) {
    raisons.push({
      cle: 'caisses-inconnues',
      code: 'CAISSE_INCONNUE',
      titre: RAISONS['CAISSE_INCONNUE']!.titre,
      detail: `${inconnues.length} libellé${inconnues.length > 1 ? 's' : ''} du rapport ne correspond${inconnues.length > 1 ? 'ent' : ''} à aucune caisse : ${inconnues.map((g) => `« ${g.alias} »`).join(', ')}.`,
      quoiFaire: RAISONS['CAISSE_INCONNUE']!.quoiFaire,
      attendu: null, calcule: null, ecart: null, brut: null,
    })
  }
  const sansMoyen = groupes.flatMap((g) => g.sansMoyen)
  if (sansMoyen.length > 0 && !codes.has('MOYEN_INCONNU') && !codes.has('MOYEN_DE_PAIEMENT_INCONNU')) {
    raisons.push({
      cle: 'moyens-inconnus',
      code: 'MOYEN_INCONNU',
      titre: RAISONS['MOYEN_INCONNU']!.titre,
      detail: `${sansMoyen.length} ligne${sansMoyen.length > 1 ? 's' : ''} n’${sansMoyen.length > 1 ? 'ont' : 'a'} pas de moyen de paiement reconnu : ${sansMoyen.map((l) => `« ${l.libelle ?? 'sans libellé'} »`).join(', ')}. Leur montant est conservé, sans jamais entrer dans une somme.`,
      quoiFaire: RAISONS['MOYEN_INCONNU']!.quoiFaire,
      attendu: null, calcule: null, ecart: null, brut: null,
    })
  }
  if (doc !== null && doc.nuit === null && !codes.has('NUIT_AMBIGUE') && !codes.has('PERIODE_ABSENTE')) {
    raisons.push({
      cle: 'nuit-absente',
      code: 'NUIT_AMBIGUE',
      titre: RAISONS['NUIT_AMBIGUE']!.titre,
      detail: 'Aucune nuit commerciale n’est rattachée à ce document : il n’apparaît donc dans aucune clôture.',
      quoiFaire: RAISONS['NUIT_AMBIGUE']!.quoiFaire,
      attendu: null, calcule: null, ecart: null, brut: null,
    })
  }
  const aVerifier = doc !== null && (doc.etat === 'NEEDS_REVIEW' || doc.etat === 'FAILED')
  if (aVerifier && raisons.length === 0) {
    raisons.push({
      cle: 'sans-raison',
      code: null,
      titre: 'Ce document est marqué à vérifier sans raison transmise',
      detail: lecture?.note ?? doc?.note ?? null,
      quoiFaire: 'Le lecteur n’a laissé ni anomalie ni note exploitable. Relancez une lecture ; si l’état ne change pas, c’est un défaut du lecteur, à signaler tel quel.',
      attendu: null, calcule: null, ecart: null, brut: null,
    })
  }

  /* ─── ce que la lecture affiche comme totaux ─── */
  const reconnues = groupes.map((g) => g.sommeReconnue).filter((s): s is number => s !== null)
  // Aucune caisse rapprochée : le total reste INCONNU. `sommeCents` rendrait 0,
  // et un tableau qui affiche « 0,00 € » a l'air d'avoir compté.
  const totalReconnu = reconnues.length === 0 ? null : sommeCents(reconnues)
  const totalOfferts = sommeCents(groupes.map((g) => g.offerts))
  const aDesOfferts = groupes.some((g) => g.lignes.some((l) => l.moyen === CODE_OFFERT))

  return (
    <main className="lc-page lc-page--large lc-stagger">
      <SmartBack />

      <header className="fr-hero">
        <div>
          <h1 className="lc-title">
            <FileText size={26} aria-hidden="true" /> {doc?.fichier ?? 'Document source'}
          </h1>
          <p className="lc-subtitle">
            {doc === null ? (
              res.etat.phase === 'chargement'
                ? 'Lecture du document en cours.'
                : 'Rien n’est affiché ici : la raison est écrite juste en dessous.'
            ) : (
              <>
                <span className={pastilleEtat(doc.etat)}>{libelleEtat(doc.etat)}</span>{' '}
                {libelleProvenance(doc.provider)} · {libelleType(doc.reportType)}
                {doc.recuLe === null ? '' : ` · reçu ${ilYA(doc.recuLe)} à ${heureParis(doc.recuLe)}`}
                {doc.ecarte ? <><br />Écarté : {doc.motifEcart ?? 'motif non transmis'} — il ne compte dans aucune nuit.</> : null}
              </>
            )}
          </p>
        </div>
        {doc !== null ? (
          <div className="fr-hero__actions">
            <VoirSource className="lc-btn lc-btn--primary"
              doc={{ id: doc.id, filename: doc.fichier, provider: doc.provider, report_type: doc.reportType, business_date: doc.nuit, received_at: doc.recuLe }}>
              Voir le PDF
            </VoirSource>
            {peutGerer ? (
              <>
                <button type="button" className="lc-btn" disabled={relecture} onClick={() => { void relire() }}>
                  {relecture ? 'Relecture en cours' : 'Relire le document'}
                </button>
                <Ecarter documentId={doc.id} retablir={doc.ecarte} onChange={res.recharger} />
              </>
            ) : null}
          </div>
        ) : null}
      </header>

      <EtatBloc etat={res.etat} quoi="ce document source" />

      {doc !== null ? (
        <>
          {/* ═══════════ POURQUOI CE DOCUMENT ATTEND QUELQU'UN ═══════════ */}
          {raisons.length > 0 ? (
            <section className="fr-section">
              <div className="fr-section__head">
                {/* Un document déjà lu peut garder un point en suspens (une nuit
                    non rattachée) sans être « à vérifier » : le titre le dit. */}
                <h2 className="fr-section__title">
                  {aVerifier ? 'Pourquoi ce document est à vérifier' : 'Ce qui reste à traiter sur ce document'}
                </h2>
                <span className="fr-section__hint">{raisons.length} point{raisons.length > 1 ? 's' : ''} à traiter</span>
              </div>
              <div className="fis-raisons">
                {raisons.map((r) => (
                  <article className="fis-raison" key={r.cle}>
                    <h3 className="fis-raison__titre">
                      <AlertTriangle size={16} aria-hidden="true" /> {r.titre}
                    </h3>
                    {r.detail !== null ? <p className="fis-raison__fait">{r.detail}</p> : null}
                    {r.attendu !== null || r.calcule !== null || r.ecart !== null ? (
                      <dl className="fis-chiffres">
                        <div className="fis-chiffre">
                          <dt>Attendu par le rapport</dt>
                          <dd><Montant cents={r.attendu} /></dd>
                        </div>
                        <div className="fis-chiffre">
                          <dt>Calculé par Gestion</dt>
                          <dd><Montant cents={r.calcule} /></dd>
                        </div>
                        <div className="fis-chiffre">
                          <dt>Écart</dt>
                          <dd><Ecart cents={r.ecart} /></dd>
                        </div>
                      </dl>
                    ) : null}
                    {r.quoiFaire !== null ? <p className="fis-raison__faire">{r.quoiFaire}</p> : null}
                    {r.brut !== null ? <p className="fis-tech fis-raison__brut">{r.brut}</p> : null}
                  </article>
                ))}
              </div>
            </section>
          ) : null}

          {/* ═══════════ CE QUE DIT LA REDDITION (Shotgun) ═══════════ */}
          {doc.reddition !== null ? <RedditionResume r={doc.reddition} statut={lecture?.statut ?? null} /> : null}

          {/* ═══════════ CE QUE L'ANALYSEUR A LU ═══════════ */}
          <section className="fr-section">
            <div className="fr-section__head">
              <h2 className="fr-section__title">Ce que l’analyseur a lu</h2>
              <span className="fr-section__hint">
                {lecture === null
                  ? 'Aucune lecture'
                  : `Lecture du ${lecture.creeLe === null ? '—' : heureParis(lecture.creeLe)}${lecture.analyseur === null ? '' : ` · ${lecture.analyseur}`}`}
              </span>
            </div>

            {lecture === null ? (
              <div className="fi-empty">
                <div className="fi-empty__title">Ce document n’a pas encore été lu</div>
                <p>
                  Il est reçu, horodaté et conservé avec son empreinte. Tant qu’une
                  lecture n’a pas eu lieu, il ne produit aucun chiffre.
                </p>
              </div>
            ) : groupes.length === 0 && doc.reportType === 'SHOTGUN_EVENT_REPORT' ? (
              // Une reddition n'a JAMAIS de lignes : c'est un contrôle, pas une
              // caisse. Le dire autrement laisserait croire à une lecture ratée.
              <div className="fi-empty">
                <div className="fi-empty__title">Aucune ligne de caisse, comme prévu</div>
                <p>
                  Une reddition contrôle la nuit, elle ne la remplit pas : l’Accueil reçoit déjà ses montants de la
                  billetterie, en tirer des lignes ici le compterait deux fois. Ses chiffres sont au-dessus.
                </p>
              </div>
            ) : groupes.length === 0 ? (
              <div className="fi-empty">
                <div className="fi-empty__title">La lecture n’a extrait aucune ligne</div>
                <p>
                  Le document a été ouvert, mais rien n’en a été retenu. Les raisons
                  ci-dessus disent ce que le lecteur a rencontré ; aucun montant n’a
                  été supposé.
                </p>
              </div>
            ) : (
              <div className="fis-caisses">
                {groupes.map((g) => (
                  <article className="fis-caisse" key={g.cle}>
                    <div className="fis-caisse__head">
                      <div className="fis-caisse__ident">
                        <h3 className="fis-caisse__nom">
                          {g.nom ?? 'Caisse inconnue'}
                          {g.nom === null ? <span className="fis-manque"> · à rattacher</span> : null}
                        </h3>
                        {g.alias !== null ? (
                          <span className="fis-caisse__alias">Libellé du rapport : {g.alias}</span>
                        ) : null}
                      </div>
                      {g.nom === null && g.alias !== null && peutMapper ? (
                        <button type="button" className="lc-btn" onClick={() => setARattacher(g.alias)}>
                          <Link2 size={16} aria-hidden="true" /> Rattacher cette caisse
                        </button>
                      ) : null}
                    </div>

                    <div className="fi-tablewrap">
                      <table className="fi-table fis-lignes">
                        <thead>
                          <tr>
                            <th scope="col">Moyen de paiement</th>
                            <th scope="col" className="fi-money">Montant lu</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.lignes.map((l) => (
                            <tr key={l.id}>
                              <td className="fr-cell-main">
                                {libelleMoyen(l.moyen)}
                                {l.libelle !== null ? (
                                  <span className="fr-cell-sub">Libellé du rapport : {l.libelle}</span>
                                ) : null}
                              </td>
                              <td className="fi-money"><Montant cents={l.montant} fort={l.moyen !== null} /></td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td>Somme des moyens de paiement lus</td>
                            <td className="fi-money">{formatMoney(g.sommeReconnue)}</td>
                          </tr>
                          {g.offerts !== 0 ? (
                            <tr>
                              <td className="fis-souspied">dont offerts</td>
                              <td className="fi-money fis-souspied">{formatMoney(g.offerts)}</td>
                            </tr>
                          ) : null}
                          {g.sansMoyen.length > 0 ? (
                            <tr>
                              <td colSpan={2} className="fis-souspied">
                                {g.sansMoyen.length} ligne{g.sansMoyen.length > 1 ? 's' : ''} sans moyen de paiement
                                reconnu {g.sansMoyen.length > 1 ? 'ne sont' : 'n’est'} pas comptée
                                {g.sansMoyen.length > 1 ? 's' : ''} dans cette somme.
                              </td>
                            </tr>
                          ) : null}
                        </tfoot>
                      </table>
                    </div>
                  </article>
                ))}
              </div>
            )}

            {groupes.length > 0 ? (
              <div className="fis-totaux">
                <div className="fis-total">
                  <span className="fi-kpi__label">Somme des moyens de paiement lus</span>
                  <span className="fi-money fi-money--fort fis-total__val">{formatMoney(totalReconnu)}</span>
                </div>
                <p className="fr-mini">
                  C’est la somme de ce qui a été LU dans ce document, caisse par
                  caisse — ni une recette arrêtée, ni un chiffre de clôture.
                </p>
              </div>
            ) : null}

            <div className="fis-notes">
            {/* Le résultat des contrôles de cohérence, dit dans les deux sens. */}
            {lecture !== null ? (
              <div className={`fr-note${lecture.statut === 'PARSED' ? '' : ' fr-note--warn'}`}>
                {lecture.statut === 'PARSED' ? <Info size={16} aria-hidden="true" /> : <AlertTriangle size={16} aria-hidden="true" />}
                <span>
                  <strong>Contrôles de cohérence : </strong>
                  {lecture.statut === 'PARSED'
                    ? doc.reportType === 'SHOTGUN_EVENT_REPORT'
                      ? 'l’analyseur n’a signalé aucun écart : ventes, frais Shotgun (frais de surclassement compris), solde et tarifs tombent juste, au centime.'
                      : 'l’analyseur n’a signalé aucun écart entre les totaux annoncés par le rapport et la somme des moyens de paiement.'
                    : 'l’analyseur a signalé au moins un point à vérifier. Les montants ci-dessus sont ceux qu’il a lus, pas ceux qu’il a validés.'}
                </span>
              </div>
            ) : null}

            {/* Les offerts : lus, affichés, et pas encore tranchés. */}
            {aDesOfferts ? (
              <div className="fr-note fr-note--warn">
                <AlertTriangle size={16} aria-hidden="true" />
                <span>
                  <strong>Offerts — traitement comptable non tranché. </strong>
                  Ces montants sont lus et affichés tels quels ({formatMoney(totalOfferts)}).
                  Le rapport ne permet pas de savoir si le total du fournisseur les
                  inclut ou les exclut : tant que la direction financière ne l’a pas
                  décidé, Gestion ne les ajoute ni ne les retranche nulle part.
                </span>
              </div>
            ) : null}
            </div>
          </section>

          {/* ═══════════ LA NUIT COMMERCIALE ═══════════ */}
          <section className="fr-section">
            <div className="fr-section__head">
              <h2 className="fr-section__title">Nuit commerciale</h2>
              <span className="fr-section__hint">Un rapport tiré au petit matin décrit la nuit de la veille.</span>
            </div>
            <div className="fis-nuit">
              <p className="fis-nuit__val">
                {doc.nuit === null
                  ? <span className="fis-manque">Aucune nuit rattachée</span>
                  : nuitLongue(doc.nuit)}
              </p>
              {/* UN RAPPORT ZAP CHANGE DE NUIT EN ENTIER (revue du 09/10/2026).
                  « Rattacher » ne déplaçait que ce fichier : la synthèse passait
                  au jeudi, ses annexes et ses tickets restaient au mercredi. Sur
                  une synthèse, c'est « Déplacer » (motif, garde-fous) ; une pièce
                  suit sa synthèse. Le serveur refuse de toute façon l'autre geste. */}
              {!peutGerer ? (
                <p className="fr-mini">Votre rôle ne permet pas de rattacher un document à une nuit.</p>
              ) : doc.rapportZap !== null && doc.rapportZap.syntheseId === doc.id ? (
                <div className="fr-actions">
                  <DeplacerRapport documentId={doc.id} nuit={doc.nuit}
                    couvertes={doc.rapportZap.nuitsCouvertes ?? (doc.nuit === null ? [] : [doc.nuit])}
                    periode={doc.rapportZap.periode} suivre={false} onChange={res.recharger} />
                  <span className="fr-mini">La synthèse change de nuit avec ses annexes, ses tickets et ses produits.</span>
                </div>
              ) : doc.rapportZap !== null && doc.rapportZap.syntheseNuit !== null ? (
                <div className="fr-actions">
                  <p className="fr-mini">
                    Cette pièce suit la synthèse de son rapport,{' '}
                    <Link href={`/comptabilite/sources/${doc.rapportZap.syntheseId}`}>« {doc.rapportZap.syntheseNom} »</Link>,
                    rangée sur la nuit du {nuitLongue(doc.rapportZap.syntheseNuit)}.
                    Pour changer la nuit du rapport, ouvrez la synthèse et choisissez « Déplacer ».
                  </p>
                  {doc.rapportZap.syntheseNuit !== doc.nuit ? (
                    <button type="button" className="lc-btn lc-btn--primary" disabled={rattachement}
                      onClick={() => { if (doc.rapportZap?.syntheseNuit) void rattacherNuit(doc.rapportZap.syntheseNuit) }}>
                      {rattachement ? 'Rattachement en cours' : `Ranger avec sa synthèse (${nuitCourte(doc.rapportZap.syntheseNuit)})`}
                    </button>
                  ) : null}
                </div>
              ) : (
                /* UNE PIÈCE DONT LA SYNTHÈSE N'A PAS DE NUIT garde le choix
                   (contre-relecture du 09/10/2026) : le serveur ne refuse
                   qu'une autre nuit que celle de la synthèse — sans nuit, il
                   n'y en a pas, et la pièce restait sans aucun geste. */
                <div className="fr-actions">
                  {doc.rapportZap !== null ? (
                    <p className="fr-mini">
                      Cette pièce appartient au rapport ZAP dont la synthèse,{' '}
                      <Link href={`/comptabilite/sources/${doc.rapportZap.syntheseId}`}>« {doc.rapportZap.syntheseNom} »</Link>,
                      n’est rangée sur aucune nuit : vous pouvez la rattacher ici.
                      « Déplacer », sur la synthèse, rangera le rapport entier.
                    </p>
                  ) : null}
                  <DatePicker
                    value={nuitChoisie ?? doc.nuit ?? aujourdhuiParis()}
                    onPick={setNuitChoisie}
                    label={`Nuit du ${nuitCourte(nuitChoisie ?? doc.nuit ?? aujourdhuiParis())}`}
                    ariaLabel="Choisir la nuit commerciale de ce document"
                  />
                  <button type="button" className="lc-btn lc-btn--primary"
                    disabled={rattachement || nuitChoisie === null || nuitChoisie === doc.nuit}
                    onClick={() => { if (nuitChoisie !== null) void rattacherNuit(nuitChoisie) }}>
                    {rattachement ? 'Rattachement en cours' : 'Rattacher à cette nuit'}
                  </button>
                </div>
              )}
            </div>
          </section>

          {/* ═══════════ LA PIÈCE ET SES LECTURES ═══════════ */}
          <section className="fr-section">
            <div className="fr-section__head">
              <h2 className="fr-section__title">La pièce et ses lectures</h2>
              <span className="fr-section__hint">Une lecture ne se corrige pas : elle se refait.</span>
            </div>
            <div className="fis-pied">
              <dl className="fr-facts">
                <div className="fr-fact">
                  <dt className="fr-fact__label">Fichier</dt>
                  <dd className="fr-fact__value">{doc.fichier}</dd>
                </div>
                <div className="fr-fact">
                  <dt className="fr-fact__label">Type de fichier</dt>
                  <dd className="fr-fact__value">{doc.mime ?? '—'}</dd>
                </div>
                <div className="fr-fact">
                  <dt className="fr-fact__label">Taille</dt>
                  <dd className="fr-fact__value">{doc.taille === null ? '—' : `${Math.round(doc.taille / 1024)} ko`}</dd>
                </div>
                <div className="fr-fact">
                  <dt className="fr-fact__label">Déposé par</dt>
                  <dd className="fr-fact__value">{doc.deposePar ?? '—'}</dd>
                </div>
                <div className="fr-fact">
                  <dt className="fr-fact__label">Mail d’origine</dt>
                  <dd className="fr-fact__value">
                    {doc.mail === null ? '—' : (doc.mail.expediteur ?? doc.mail.sujet ?? doc.mail.identifiant ?? '—')}
                  </dd>
                </div>
                <div className="fr-fact">
                  <dt className="fr-fact__label">Empreinte</dt>
                  <dd className="fr-fact__value fis-tech">{doc.empreinte ?? '—'}</dd>
                </div>
              </dl>

              <ul className="fis-lectures">
                {doc.lectures.length === 0 ? (
                  <li className="fr-mini">Aucune lecture n’a encore été faite sur ce document.</li>
                ) : null}
                {doc.lectures.map((l, i) => (
                  <li key={l.id}>
                    <span className={pastilleEtat(l.statut)}>{libelleEtat(l.statut)}</span>
                    <span className="fis-lectures__quand">
                      {l.creeLe === null ? '—' : `${ilYA(l.creeLe)} · ${heureParis(l.creeLe)}`}
                    </span>
                    <span className="fis-tech">{l.analyseur ?? 'analyseur non transmis'}</span>
                    <span className="fr-mini">
                      {i === 0 ? 'lecture en vigueur' : 'lecture précédente'} · {l.lignes.length} ligne{l.lignes.length > 1 ? 's' : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </>
      ) : null}

      {aRattacher !== null && doc !== null ? (
        <ModaleCorrespondance
          alias={aRattacher}
          provider={doc.provider}
          caisses={refs.etat.phase === 'ok' ? refs.etat.data : []}
          referentielLu={refs.etat.phase === 'ok'}
          onFermer={() => setARattacher(null)}
          onFait={() => {
            setARattacher(null)
            if (peutGerer) void relire()
            else res.recharger()
          }}
        />
      ) : null}
    </main>
  )
}

/* ═══════════════════════ rattacher une caisse inconnue ═══════════════════ */

/**
 * LA CORRESPONDANCE EST UNE DÉCISION HUMAINE.
 *
 * Gestion ne rapproche jamais deux libellés parce qu'ils se ressemblent : c'est
 * la personne qui déclare que « Caisse Bar … 2 » désigne telle caisse. La case
 * « Enregistrer cette correspondance pour l'avenir » n'est pas un confort : la
 * mémorisation est le SEUL chemin qui existe. Une lecture déjà écrite ne se
 * modifie pas ; c'est la table des alias qui, à la lecture suivante, résout le
 * libellé. Décocher la case ne laisse donc rien à appliquer — et l'écran le dit
 * plutôt que d'offrir un bouton qui ne ferait rien.
 */
function ModaleCorrespondance(props: {
  alias: string
  provider: string
  caisses: Caisse[]
  referentielLu: boolean
  onFermer: () => void
  onFait: () => void
}) {
  const toast = useToast()
  const [cible, setCible] = useState<string | null>(null)
  const [memoriser, setMemoriser] = useState(true)
  const [envoi, setEnvoi] = useState(false)

  const enregistrer = async () => {
    if (cible === null) return
    setEnvoi(true)
    const r = await adminMutate<{ error?: string; message?: string }>(
      'POST', `/api/v1/finance/references/registers/${cible}/aliases`,
      { provider: props.provider, provider_label: props.alias },
    )
    setEnvoi(false)
    if (r.status >= 200 && r.status < 300) {
      toast('ok', 'Correspondance enregistrée. Le document est relu pour qu’elle s’applique.')
      props.onFait()
      return
    }
    toast('error', r.data.message ?? 'La correspondance a été refusée par le serveur.')
  }

  return (
    <Modal open onClose={props.onFermer} owning ariaLabel="Rattacher une caisse du rapport" width={620}>
      <header className="lc-modal__head">
        <div>
          <div className="lc-modal__title">Rattacher une caisse du rapport</div>
          <div className="fr-mini">
            Le libellé du fournisseur reste affiché en second rang : il ne devient
            jamais le nom courant de la caisse.
          </div>
        </div>
      </header>
      <div className="lc-modal__body">
        <div className="fr-fields">
          <div>
            <span className="lc-label">Libellé lu dans le rapport</span>
            <p className="fis-alias">{props.alias}</p>
          </div>
          <div>
            <span className="lc-label">Caisse de Gestion</span>
            <Combobox
              value={cible}
              items={props.caisses.map((c) => ({ value: c.id, label: c.nom, ...(c.pointDeVente === null ? {} : { hint: c.pointDeVente }) }))}
              onChange={setCible}
              placeholder={props.caisses.length === 0 ? 'Aucune caisse lisible' : 'Choisir une caisse'}
              ariaLabel="Caisse à laquelle rattacher ce libellé"
              clearable
              disabled={props.caisses.length === 0}
            />
            {props.referentielLu && props.caisses.length === 0 ? (
              <p className="fr-mini">
                Le référentiel ne contient aucune caisse : créez-la d’abord dans
                Comptabilité → Référentiels.
              </p>
            ) : null}
            {!props.referentielLu ? (
              <p className="fr-mini">
                Le référentiel des caisses n’a pas pu être lu : sans lui, aucune
                correspondance ne peut être proposée.
              </p>
            ) : null}
          </div>
          <div>
            <Checkbox
              checked={memoriser}
              onChange={setMemoriser}
              label="Enregistrer cette correspondance pour l’avenir"
            />
            <p className="fr-mini">
              {memoriser
                ? 'Les prochains rapports qui portent ce libellé se résoudront seuls, sans repasser par cet écran.'
                : 'Sans mémorisation, il n’y a rien à appliquer : une lecture déjà écrite ne se modifie pas, c’est la correspondance enregistrée qui résout le libellé à la lecture suivante.'}
            </p>
          </div>
        </div>
        <div className="fr-actions fr-actions--end">
          <button type="button" className="lc-btn lc-btn--ghost" onClick={props.onFermer}>Annuler</button>
          <button type="button" className="lc-btn lc-btn--primary"
            disabled={envoi || cible === null || !memoriser}
            onClick={() => { void enregistrer() }}>
            {envoi ? 'Enregistrement en cours' : 'Rattacher et relire'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
